// src/components/Footer.js

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { BsTwitterX } from "react-icons/bs";
import {
  FiGithub,
  FiLinkedin,
  FiMail,
  FiArrowUp,
  FiMapPin,
  FiPhone
} from 'react-icons/fi';

import NewsletterForm from './NewsletterForm'; // Importar el nuevo componente
import logoSvg from '../assets/logo.svg';

const FooterContainer = styled.footer`
  background-color: var(--primary);
  color: var(--white);
  padding-top: var(--spacing-xl);
`;

const FooterContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xl);
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const FooterColumn = styled.div`
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-md);
  
  img {
    height: 24px;
    width: auto;
  }
`;

const FooterDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin-bottom: var(--spacing-md);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-sm);
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: var(--white);
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--accent);
    transform: translateY(-3px);
  }
`;

const FooterTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: var(--accent);
    border-radius: 1.5px;
  }
`;

const FooterLinks = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: var(--spacing-sm);
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color var(--transition-fast);
    display: flex;
    align-items: center;
    
    &:hover {
      color: var(--accent);
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateX(4px);
    
    svg {
      color: var(--accent);
    }

    p, a {
      color: var(--accent);
    }
  }
  
  svg {
    margin-right: var(--spacing-sm);
    margin-top: 4px;
    color: var(--accent);
    transition: color var(--transition-fast);
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    transition: color var(--transition-fast);
  }
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color var(--transition-fast);
    
    &:hover {
      color: var(--accent);
    }
  }
`;


const BottomBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: var(--spacing-xl);
  gap: var(--spacing-md);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-md);
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0;
`;

const BottomLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  
  a {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    text-decoration: none;
    transition: color var(--transition-fast);
    
    &:hover {
      color: var(--accent);
    }
  }
`;

const ScrollToTop = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background-color: var(--primary);
  color: var(--white);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  z-index: 10;
  
  &:hover {
    background-color: var(--accent);
    transform: translateY(-3px);
  }
  
  @media (max-width: 576px) {
    bottom: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <img src={logoSvg} alt="XADevs" />
          </FooterLogo>
          <FooterDescription>
            XADevs LLC builds AI products and software systems with a focus on practical products, strong UX, and reliable engineering.
          </FooterDescription>

          <SocialLinks>
            <SocialLink href="https://github.com/xadevs" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FiGithub />
            </SocialLink>
            <SocialLink href="https://x.com/xadevs" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
              <BsTwitterX />
            </SocialLink>
            <SocialLink href="https://linkedin.com/company/xadevs" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FiLinkedin />
            </SocialLink>
            <SocialLink href="mailto:hello@xadevs.com" aria-label="Email">
              <FiMail />
            </SocialLink>
          </SocialLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Quick Links</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/">Home</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/#products">Products</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/#about">About</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/#contact">Contact</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/support">Support</Link>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Products</FooterTitle>
          <FooterDescription>
            VolpeTrack is available now as an AI-powered personal finance assistant for tracking expenses and planning goals.
          </FooterDescription>
          <FooterDescription>
            HoldThen is in active rollout for international logistics operations. Subscribe for product and release updates.
          </FooterDescription>
          {/* Reemplazar el formulario HTML estático por el componente NewsletterForm */}
          <NewsletterForm />
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Contact Us</FooterTitle>
          <ContactItem>
            <FiMail />
            <p><a href="mailto:hello@xadevs.com">hello@xadevs.com</a></p>
          </ContactItem>
          <ContactItem>
            <BsTwitterX />
            <p><a href="https://x.com/xadevs" target="_blank" rel="noopener noreferrer">@XADevs</a></p>
          </ContactItem>
          <ContactItem>
            <FiMapPin />
            <p>
              30 N Gould St #N<br />
              Sheridan, WY 82801, USA
            </p>
          </ContactItem>
          <ContactItem>
            <FiPhone />
            <p>
              +1 307 556 7770
            </p>
          </ContactItem>

          <FooterTitle>Legal</FooterTitle>

          <FooterLinks>
            <FooterLink>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/terms-of-service">Terms of Service</Link>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>
      </FooterContent>

      <FooterContent>
        <BottomBar>
          <Copyright>
            &copy; {currentYear} XADevs LLC. All rights reserved.
          </Copyright>
          <BottomLinks>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-of-service">Terms</Link>
            <Link to="/sitemap">Sitemap</Link>
          </BottomLinks>
        </BottomBar>
      </FooterContent>

      <ScrollToTop onClick={scrollToTop} aria-label="Scroll to top">
        <FiArrowUp />
      </ScrollToTop>
    </FooterContainer>
  );
};

export default Footer;
